import { useRef, useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { USING_STUBS } from '../../api';
import { toApiError } from '../../api/errors';
import type { AgentUsage, TenantUsage, UsageAgent } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Link } from '../../router/Router';
import type { AccountRoute } from '../../router/routes';
import { Ago } from '../../ui/Ago';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { DataTable } from '../../ui/DataTable';
import type { Column } from '../../ui/DataTable';
import { Button, Card, Chip, EmptyState, PageHead, Stat } from '../../ui/primitives';
import { IconChevronLeft } from '../../ui/icons';
import { StubUsageHint } from '../../stubs/StubBanner';
import {
  agentLines,
  agentsFigure,
  byKindEntries,
  checkPeriod,
  durationParts,
  emptyPeriodForm,
  monthBefore,
  monthOf,
  peopleFigure,
  peopleLine,
  periodOf,
  resourcesFigure,
  sessionsFigure,
  unknownGaps,
  usageFailure,
} from './usage';
import type { Figure, PeriodForm, UsageFailure } from './usage';

/**
 * What one tenancy used, over a period somebody chose.
 *
 * Three properties are the whole screen.
 *
 * **The figures stand from the first moment.** Not read yet is a state of a
 * figure, not an empty screen with an instruction on it: the four boxes are
 * there before anything is asked for, so what this screen is for is legible
 * without pressing anything.
 *
 * **A figure that is missing is drawn as missing.** Sessions and people arrive
 * `null` with a reason today, because the control plane cannot reach the audit
 * export they are counted from, and that is an ordinary answer rather than a
 * fault. Every figure goes through `Figure<T>`, so there is no path here that
 * renders an absent one as a number — a zero would be a figure somebody could
 * be invoiced against, and a dash would be a shrug where a reason exists.
 *
 * **Nothing refreshes on its own.** The reading is asked for and not polled:
 * two of the four figures are questions put to the tenancy's own cluster, and
 * that cluster records the asking in its own audit trail under the name of
 * whoever asked. A screen on a timer would fill a customer's trail with this
 * console's curiosity, so there is no timer, no interval and no reload on
 * focus.
 */
export function UsageScreen({ slug, back }: { slug: string; back?: AccountRoute }) {
  const { t } = useI18n();
  const api = useAccountApi();

  const [form, setForm] = useState<PeriodForm>(emptyPeriodForm);
  /** Complaints appear once somebody has tried, not while they are still typing. */
  const [tried, setTried] = useState(false);
  const [reading, setReading] = useState<Reading>({ status: 'idle' });
  /** Which request the screen is showing, so a slow answer cannot land on a newer one. */
  const attempt = useRef(0);

  const errors = checkPeriod(form);
  const period = periodOf(form);

  async function read(): Promise<void> {
    setTried(true);
    // A half-filled choice is not sent: there is no half of a period, and the
    // server would refuse it after somebody had already waited.
    if (period === null) return;

    const token = attempt.current + 1;
    attempt.current = token;
    setReading({ status: 'reading' });

    try {
      const usage = await api.provider.readUsage(slug, period);
      if (attempt.current === token) setReading({ status: 'read', usage });
    } catch (error: unknown) {
      if (attempt.current === token) {
        setReading({ status: 'refused', failure: usageFailure(toApiError(error)) });
      }
    }
  }

  const busy = reading.status === 'reading';
  const usage = reading.status === 'read' ? reading.usage : undefined;

  return (
    <div className="content-inner">
      {/* Back to the card these figures belong to, where there is one. A
          tenancy's own representative came from their own screen and has no
          card of the operator's to go back to. */}
      {back !== undefined && (
        <div style={{ marginBottom: 18 }}>
          <Link to={back} className="btn btn-ghost btn-sm">
            <IconChevronLeft size={16} />
            {t('usage.back')}
          </Link>
        </div>
      )}

      <PageHead title={t('usage.title')} />

      <div className="stack">
        <div className="period-bar">
          <Button
            size="sm"
            onClick={() => {
              setForm(monthBefore(new Date()));
            }}
          >
            {t('usage.period.lastMonth')}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setForm(monthOf(new Date()));
            }}
          >
            {t('usage.period.thisMonth')}
          </Button>
          <span className="sep" aria-hidden="true">
            │
          </span>
          <DateBox
            label={t('usage.period.from.short')}
            value={form.from}
            onChange={(next) => {
              setForm({ ...form, from: next });
            }}
          />
          <DateBox
            label={t('usage.period.to.short')}
            value={form.to}
            onChange={(next) => {
              setForm({ ...form, to: next });
            }}
          />
          {/* Enabled even when the choice is incomplete, and pressing it then
              sends nothing: it names the box that is empty. A button that is
              merely inert says the screen is broken, and a period with one end
              is the commonest way to get here. */}
          <Button
            variant="primary"
            size="sm"
            disabled={busy}
            onClick={() => {
              void read();
            }}
          >
            {busy
              ? `${t('usage.submitting')}…`
              : t(reading.status === 'read' ? 'usage.again' : 'usage.submit')}
          </Button>
        </div>

        {tried && (errors.from !== undefined || errors.to !== undefined) && (
          <p className="err">
            <span aria-hidden="true">✕</span>
            {t(errors.from ?? errors.to ?? 'usage.check.fromRequired')}
          </p>
        )}

        {USING_STUBS && (
          <StubUsageHint
            onChanged={() => {
              // The switch changes what a reading would answer, not the reading
              // on screen. Whoever flipped it presses the button again — this
              // route is not one anything re-reads on its own.
              setReading({ status: 'idle' });
            }}
          />
        )}

        <Figures usage={usage} busy={busy} />

        {reading.status === 'refused' && (
          <Refusal failure={reading.failure} onRetry={() => void read()} />
        )}

        {usage !== undefined && <Sessions usage={usage} />}
        {usage !== undefined && <Connectors figure={agentsFigure(usage)} />}
        {usage !== undefined && <Strays usage={usage} />}
      </div>
    </div>
  );
}

/** What the screen is showing. `idle` is a state, not an empty result. */
type Reading =
  | { status: 'idle' }
  | { status: 'reading' }
  | { status: 'read'; usage: TenantUsage }
  | { status: 'refused'; failure: UsageFailure };

/** One end of the period, in the row rather than in a form. */
function DateBox({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="inline-field">
      <span className="faint">{label}</span>
      <input
        type="date"
        className="cell-select"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </label>
  );
}

function Refusal({ failure, onRetry }: { failure: UsageFailure; onRetry: () => void }) {
  const { t } = useI18n();

  if (failure.kind === 'other') {
    return (
      <ErrorAlert
        error={failure.error}
        action={
          <div style={{ marginTop: 12 }}>
            <Button size="sm" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          </div>
        }
      />
    );
  }

  // A tenancy that is not there and one this account is not entitled to read the
  // same, deliberately: anything else would answer "which of our customers is
  // this" to whoever was guessing.
  return (
    <Card>
      <EmptyState title={t(failure.titleKey)} body={t(failure.bodyKey)} />
    </Card>
  );
}

/**
 * The four figures an invoice is worked out from — standing before anything has
 * been asked for, so that the screen says what it is for.
 *
 * The two live ones carry the moment they were read in the tile itself: side by
 * side with two figures counted over a window, an unqualified number reads as
 * if it were one of them.
 */
function Figures({ usage, busy }: { usage: TenantUsage | undefined; busy: boolean }) {
  const { t, formatNumber, formatDateTime } = useI18n();

  const unread = { label: t(busy ? 'common.loading' : 'common.unread') };

  if (usage === undefined) {
    return (
      <div className="grid g-4">
        <Stat label={t('usage.figure.connectors')} absent={unread} />
        <Stat label={t('usage.figure.resources')} absent={unread} />
        <Stat label={t('usage.figure.sessions')} absent={unread} />
        <Stat label={t('usage.figure.people')} absent={unread} />
      </div>
    );
  }

  const connectors = agentsFigure(usage);
  const resources = resourcesFigure(usage);
  const sessions = sessionsFigure(usage);
  const people = peopleFigure(usage);

  return (
    <>
      <div className="grid g-4">
        {connectors.counted ? (
          <Stat
            label={t('usage.figure.connectors')}
            value={formatNumber(connectors.value.active)}
            sub={t('usage.asOf', { when: formatDateTime(connectors.value.as_of) })}
          />
        ) : (
          <Stat label={t('usage.figure.connectors')} absent={missing(t, connectors.reason)} />
        )}

        {resources.counted ? (
          <Stat
            label={t('usage.figure.resources')}
            value={formatNumber(resources.value.count)}
            sub={t('usage.asOf', { when: formatDateTime(resources.value.as_of) })}
          />
        ) : (
          <Stat label={t('usage.figure.resources')} absent={missing(t, resources.reason)} />
        )}

        {sessions.counted ? (
          <Stat label={t('usage.figure.sessions')} value={formatNumber(sessions.value.count)} />
        ) : (
          <Stat label={t('usage.figure.sessions')} absent={missing(t, sessions.reason)} />
        )}

        {people.counted ? (
          <Stat
            label={t('usage.figure.people')}
            value={formatNumber(peopleLine(people.value).count)}
            sub={t('usage.people.rows.short', { rows: peopleLine(people.value).rowsNote.rows })}
          />
        ) : (
          <Stat label={t('usage.figure.people')} absent={missing(t, people.reason)} />
        )}
      </div>

      {resources.counted && byKindEntries(resources.value.by_kind).length > 0 && (
        <div className="row">
          <span className="faint">{t('usage.resources.byKind')}</span>
          {byKindEntries(resources.value.by_kind).map((entry) => (
            <Chip key={entry.kind}>
              {entry.kind}
              {' · '}
              {formatNumber(entry.count)}
            </Chip>
          ))}
        </div>
      )}
    </>
  );
}

/** A figure the answer could not count, with the server's reason in its place. */
function missing(
  t: (key: 'usage.missing.title' | 'usage.missing.noReason') => string,
  reason: string | undefined,
): { label: string; reason: string } {
  return { label: t('usage.missing.title'), reason: reason ?? t('usage.missing.noReason') };
}

/**
 * The sessions that opened inside the window, in the five figures that make up
 * the first one.
 *
 * `recorded_bytes` is labelled as recording throughout, and the label holds for
 * the case that would otherwise make it look broken: a database, Kubernetes,
 * MCP or plain TCP session closes at zero bytes however much traffic crossed
 * it, because what gdsgate measures there is the size of the recording.
 */
function Sessions({ usage }: { usage: TenantUsage }) {
  const { t, formatNumber } = useI18n();

  const figure = sessionsFigure(usage);
  if (!figure.counted) return null;

  const sessions = figure.value;
  const parts = durationParts(sessions.duration_secs);

  return (
    <>
      <div className="grid g-3">
        <Stat label={t('usage.sessions.closed')} value={formatNumber(sessions.closed)} />
        <Stat label={t('usage.sessions.unclosed')} value={formatNumber(sessions.unclosed)} />
        <Stat
          label={t('usage.sessions.duration')}
          value={t('usage.sessions.duration.parts', {
            hours: parts.hours,
            minutes: parts.minutes,
            seconds: parts.seconds,
          })}
        />
        <Stat
          label={t('usage.sessions.recorded')}
          value={formatNumber(sessions.recorded_bytes)}
          sub={t('usage.bytes')}
        />
        <Stat
          label={t('usage.sessions.orphan')}
          value={formatNumber(sessions.closed_without_open)}
          {...(sessions.closed_without_open > 0 ? { tone: 'warn' as const } : {})}
        />
      </div>

      {byKindEntries(sessions.by_kind).length > 0 && (
        <div className="row">
          <span className="faint">{t('usage.sessions.byKind')}</span>
          {byKindEntries(sessions.by_kind).map((entry) => (
            <Chip key={entry.kind}>
              {entry.kind}
              {' · '}
              {formatNumber(entry.count)}
            </Chip>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * The connectors, as of the moment the registry was read.
 *
 * The name a node gave itself carries its mark in every row. It sits beside a
 * checked identifier, and unmarked it would be read as if somebody had checked
 * it too — which is why the mark is here rather than in a footnote under the
 * table.
 */
function Connectors({ figure }: { figure: Figure<AgentUsage> }) {
  const { t, formatNumber } = useI18n();

  if (!figure.counted) return null;

  const lines = agentLines(figure.value);
  const columns: Column<UsageAgent>[] = [
    {
      key: 'id',
      header: t('usage.connectors.col.id'),
      mono: true,
      sort: (a, b) => a.id.localeCompare(b.id),
      cell: (connector) => connector.id,
    },
    {
      key: 'claimed',
      header: t('usage.connectors.col.claimed'),
      sort: (a, b) => (a.claimed_name ?? '').localeCompare(b.claimed_name ?? ''),
      cell: (connector) => {
        const line = lines.find((one) => one.id === connector.id);
        if (line?.claimed === undefined) {
          return <span className="faint">{t('usage.connectors.claimed.none')}</span>;
        }
        return (
          <span className="row">
            <span className="mono">{line.claimed.name}</span>
            <Chip>{t(line.claimed.markKey)}</Chip>
          </span>
        );
      },
    },
    {
      key: 'enrolled',
      header: t('usage.connectors.col.enrolled'),
      sort: (a, b) => (a.enrolled_at ?? '').localeCompare(b.enrolled_at ?? ''),
      cell: (connector) =>
        connector.enrolled_at === undefined ? (
          <span className="faint">{t('usage.connectors.enrolled.unknown')}</span>
        ) : (
          <Ago at={connector.enrolled_at} />
        ),
    },
  ];

  return (
    <Card>
      <div className="card-head">
        <span className="ct">{t('usage.connectors.section')}</span>
        <span className="ca faint">
          {t('usage.connectors.revoked')}
          {': '}
          {formatNumber(figure.value.revoked)}
        </span>
      </div>
      <div className="card-pad">
        <DataTable<UsageAgent>
          caption={t('usage.connectors.section')}
          columns={columns}
          rows={figure.value.connectors}
          rowKey={(connector) => connector.id}
          initialSort={{ key: 'id', direction: 'asc' }}
          empty={{ title: t('usage.connectors.none') }}
          noMatch={{ title: t('usage.connectors.none') }}
        />
      </div>
    </Card>
  );
}

/** A figure this console does not draw that the answer could not count either. */
function Strays({ usage }: { usage: TenantUsage }) {
  const { t } = useI18n();

  const strays = unknownGaps(usage);
  if (strays.length === 0) return null;

  return (
    <Alert tone="warn" title={t('usage.missing.title')}>
      {strays.map((gap) => (
        <p key={gap.figure}>
          {t('usage.gap.unknown', { figure: gap.figure })}
          <span className="mono"> {gap.reason}</span>
        </p>
      ))}
    </Alert>
  );
}

